import React, { useState, useEffect } from 'react';
import Portal from '../common/Portal';

const UpdateBreedingStatusModal = ({ isOpen, onClose, onSubmit, isLoading, event }) => {
    const [newStatus, setNewStatus] = useState('');
    const [calvedDate, setCalvedDate] = useState('');
    // This effect runs when the modal is opened or the event data changes.
    // It sets the initial status from the event.
    useEffect(() => {
        if (event) {
            setNewStatus(event.status || '');
            // Reset calved date when modal opens to ensure a clean state
            setCalvedDate(''); 
        }
    }, [event]);

    // This new effect watches for changes in the status dropdown.
    // If the user selects 'Completed (Calved)', it autofills the date.
    useEffect(() => {
        if (newStatus === 'Completed (Calved)') {
            // Get today's date in YYYY-MM-DD format for the input
            const today = new Date().toISOString().split('T')[0];
            setCalvedDate(today);
        }
    }, [newStatus]); // Reruns whenever 'newStatus' changes

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        // Validate that the calved date is present if the event is completed.
        if (newStatus === 'Completed (Calved)' && !calvedDate) {
            alert('Please provide the calved date to complete the event.');
            return;
        }
        // Pass all the necessary data to the handler function in FarmManager
        onSubmit(event.id, newStatus, { calvedDate });
    };

    return (
        <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md m-4">
                    <h2 className="text-2xl font-bold mb-4 text-gray-800">Update Breeding Event</h2>

                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                        <h3 className="text-md font-semibold text-gray-700 mb-2">Event Summary</h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <span className="text-gray-500">Dam (Mother):</span>
                            <span className="font-medium text-gray-900">{event.animalName}</span>
                            
                            <span className="text-gray-500">Sire (Father):</span>
                            <span className="font-medium text-gray-900">{event.sire || 'N/A'}</span>

                            <span className="text-gray-500">Breeding Type:</span>
                            <span className="font-medium text-gray-900">{event.eventType || 'N/A'}</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">New Status</label>
                                <select 
                                    name="status" 
                                    id="status" 
                                    value={newStatus} 
                                    onChange={(e) => setNewStatus(e.target.value)} 
                                    className="mt-1 block w-full form-select rounded-md shadow-sm"
                                >
                                    <option>In-Progress</option>
                                    <option>Confirmed</option>
                                    <option>Due</option>
                                    <option>Failed</option>
                                    <option>Completed (Calved)</option>
                                </select>
                            </div>
                            
                            {/* This section now appears with the date autofilled */}
                            {newStatus === 'Completed (Calved)' && (
                                <div>
                                    <label htmlFor="calvedDate" className="block text-sm font-medium text-gray-700">
                                        Calved Date (DOB of new animal)
                                    </label>
                                    <input
                                        type="date"
                                        id="calvedDate"
                                        value={calvedDate}
                                        onChange={(e) => setCalvedDate(e.target.value)}
                                        className="mt-1 block w-full form-input rounded-md shadow-sm"
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        <div className="mt-8 flex justify-end space-x-3">
                            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 flex items-center">
                                {isLoading ? 'Updating...' : 'Update Status'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Portal>
    );
};

export default UpdateBreedingStatusModal;